"use client";

import {
	ProductCard,
	type ProductCardProduct
} from "@pamperedchef/consumer-components/product";
import { useCallback } from "react";

import { type AddToCartSource } from "@/app/components/shared/recommended-products/add-to-cart-event";
import { useAddToCartSlider } from "@/app/hooks/useAddToCartSlider";
import { useAddToCartErrorStore } from "@/app/store";

const QUANTITY = 1;

export interface ProductCardWithCartProps {
	product: ProductCardProduct;
	/** The surface the card sits on, reported with every add. */
	source: AddToCartSource;
	/** Set when the product cannot be bought; shown in place of the button. */
	statusLabel?: string;
	zoomOnHover?: boolean;
}

/**
 * The PDP's add-to-cart path on a card: the slider on success and the shared
 * error banner on failure. The hook is keyed to one sku, so it lives per card.
 */
export function ProductCardWithCart({
	product,
	source,
	statusLabel,
	zoomOnHover
}: ProductCardWithCartProps) {
	const clearError = useAddToCartErrorStore((state) => state.clear);

	const salePrice = product.price?.display ?? 0;
	const { onClick, state } = useAddToCartSlider({
		sku: product.sku,
		quantity: QUANTITY,
		source,
		product: {
			sku: product.sku,
			quantity: QUANTITY,
			name: product.title,
			imageUrl: product.image.src,
			listPrice: product.price?.strikeThrough ?? salePrice,
			salePrice
		}
	});

	const handleAddToCart = useCallback(() => {
		clearError();
		onClick();
	}, [clearError, onClick]);

	if (statusLabel) {
		return (
			<ProductCard
				product={product}
				statusLabel={statusLabel}
				zoomOnHover={zoomOnHover}
			/>
		);
	}

	return (
		<ProductCard
			product={product}
			zoomOnHover={zoomOnHover}
			action={{
				label: "Add to Cart",
				reserveLabel: "Adding…",
				isPending: state.loading,
				isDisabled: state.loading,
				onPress: handleAddToCart
			}}
		/>
	);
}
